import React, {FC, useEffect, useState} from 'react';

import {Item} from "./Item";
import {useAppSelector} from "../../../../../redux/hooks";
import Skeleton from "react-loading-skeleton";
import {Button} from "../../../../buttons";
import plus from "../../../../../assets/icons/commons/roundPlus.svg";
import {t} from "i18next";
import store from "../../../../../redux/store";
import {
  saveModuleSettings,
  setIsOrdersProcessingRulesSubmitting,
  setIsOrdersSettingsFormSubmitting,
  updateOrdersProcessingRules,
} from "../../../../../redux/orders/reducer";
import ordersService from "../../../../../api/services/ordersService";
import {Filter, Warehouse} from "../../OrdersSettingsFormWrapper";
import sellerService from "../../../../../api/services/sellerService";
import warehousesService from "../../../../../api/services/warehousesService";
import {setRowData} from "../../../../../redux/warehouses/reducer";
import {parseWarehouseType} from "../../../../Tables/WarehouseTable/parse-warehouse-type";



export interface Rule {
  id: string|null // "7b233cb1-5a37-4fba-b57a-aa230f22ad01"
  isActive: boolean //false
  filter: Filter
  isAutoChangeToConfirmed: boolean //false
  isAutoChangeToShipping: boolean //false
  isAutoChangeToDelivery: boolean //false
  autoCancellationIfOutOfStock: string|null // "partial"
  minimalStock: number // 1
  isDisclosed: boolean //false
}

const initialRule: Rule = {
  id: null,
  isActive: false,
  filter: {},
  isAutoChangeToConfirmed: false,
  isAutoChangeToShipping: false,
  isAutoChangeToDelivery: false,
  autoCancellationIfOutOfStock: null,
  minimalStock: 0,
  isDisclosed: false
}

interface OrderProcessingRulesProps {
  toSubmit: boolean,
}

const OrderProcessingRules: FC<OrderProcessingRulesProps> = ({toSubmit}) => {
  const [isLoadingRules, setIsLoadingRules] = useState<boolean>(false);
  const [rules, setRules] = useState<Rule[]>([]);

  const [connectedMarkets, setConnectedMarkets] = useState<string[]>([])
  const [connectedWarehouses, setConnectedWarehouses] = useState<Warehouse[]>([])

  const { sellerId, isOrdersSettingsFormSubmitting } = useAppSelector(({ sellers, orders }) => ({
    sellerId: sellers.currentSeller.info.data?.id,
    isOrdersSettingsFormSubmitting: orders.isOrdersSettingsFormSubmitting,
  }))

  useEffect(() => {
    // /api/v2/sellers/connected-marketplaces

    if (sellerId) {
      // Prepare data
      setIsLoadingRules(true)
      ordersService.getSellerOrdersProcessingRules(sellerId)
        .then(
          (res) => {
            if (res.data.success) {
              const rules = res.data.data.map((rule: Rule) => {
                rule['isDisclosed'] = false
                return rule
              })
              console.log("res.data.data", rules)
              setRules(rules)
            }
          },
          (err) => {
            setRules([])
            alert('Failed getting processing rules: ' + err.message())
          }
        )
        .catch((err) => {
          setRules([])
          alert('Failed getting processing rules: ' + err.message)
        })
        .finally(() => setIsLoadingRules(false))


      // Prepare connected markets
      setIsLoadingRules(true)
      sellerService.getConnectedMarkets(sellerId)
          .then(
              (res) => {
                if (res.data.success) {
                  const ms = Object.keys(res.data.data).map((id: string) => id)
                  setConnectedMarkets(ms)
                }
              },
              (err) => {
                setConnectedMarkets([])
                alert('Failed getting connected markets: ' + err.message())
              }
          )
          .catch((err) => {
            setConnectedMarkets([])
            alert('Failed getting connected markets: ' + err.message)
          })

      // Prepare warehouses
      warehousesService.getWarehousesForSeller(sellerId)
          .then(
              (res) => {
                if (res.data.success) {
                  const filteredRows = res.data.data.filter((v:any) => v.status === 'active')
                  const sortedRows = filteredRows.sort(
                      (a: any, b: any) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)),
                  );
                  const ms = sortedRows.map(
                      (wh: {uuid: string, name: string, type: string, externalService: string}) => {
                        return {
                          id: wh.uuid,
                          //@ts-ignore
                          name: wh.name + ' - ' + t(parseWarehouseType(wh.type)) + ' ' + t(wh.externalService)
                        }
                      }
                  )
                  setConnectedWarehouses(ms)
                }
              },
              (err) => {
                setConnectedWarehouses([])
                alert('Failed getting warehouses: ' + err.message())
              }
          )
          .catch((err) => {
            setConnectedWarehouses([])
            alert('Failed getting warehouses: ' + err.message)
          })
      ;

    } else {
      setRules([])
      setConnectedMarkets([])
      setConnectedWarehouses([])
    }
    
  }, [])

  useEffect(() => {
    if (toSubmit) {
      submitHandler()
    }
  }, [toSubmit])

  function toggleIsDisclosed(index: number) {
    const updateRules = (collection: any, item: Rule, currentIndex: number) => {
      return [
        ...collection,
        {
          ...item,
          isDisclosed: currentIndex === index ? !item.isDisclosed : false,
        },
      ]
    };

    setRules((prevState) => prevState.reduce(updateRules, []));
  }

  function removeRule(index: number) {
    setRules((prevState) => prevState.filter((_, i) => i !== index));
  }

  function updateDataHandler(index: number, data: Rule|null) {
    if (data === null) {
      removeRule(index)
    } else if (rules[index] === undefined) {
      setRules([data, ...rules])
    } else {
      rules[index] = data
      setRules(rules)
    }

    console.log('Data. updateDataHandler: ', data, index)
  }

  const renderItem = (r: Rule, k: number) => {
    return <Item
        key={k}
        index={k}
        label={`# ${rules.length - k} Автомат`}
        data={r}
        updateDataHandler={updateDataHandler}
        toggleIsDisclosed={toggleIsDisclosed}
        isDisclosed={r.isDisclosed}
        connectedMarkets={connectedMarkets}
        connectedWarehouses={connectedWarehouses}
    />
  };

  const toShowRuleForm = () => {
    setRules([initialRule, ...rules])
    toggleIsDisclosed(0)
  }

  const submitHandler = () => {
    store.dispatch(setIsOrdersProcessingRulesSubmitting(true))
    setTimeout(() => {
      store.dispatch(setIsOrdersProcessingRulesSubmitting(false))
    }, 500)
    if (rules.length === 0) {
      return
    }
    //@ts-ignore
    store.dispatch(updateOrdersProcessingRules({sellerId: sellerId, rules: rules}))
  }

  return (
      <>
        <div className={`flex justify-end`}>
          <Button
              clickHandler={toShowRuleForm}
              appearance="primary"
              customClassName="button-add"
          >
            <div className="flex justify-around w-[150px] items-center">
              <img src={plus} className="w-4 h-4" />
              <div>Новое правило</div>
            </div>
          </Button>
        </div>

        {isLoadingRules && <Skeleton containerClassName={`w-full h-[38px] flex mt-4`}></Skeleton>}

        {!isLoadingRules && rules.length === 0 && <p className={`text-[#94A3B8] mt-3`}>У вас пока нет правил обработки заказов</p>}

        {rules.map(renderItem)}

      </>
  );
};

export default OrderProcessingRules;
